import type { ReactNode } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { CalendarClock, Camera, Download, FileText } from 'lucide-react';
import { PublicHeader } from '@/components/PublicHeader';
import { useCurrentUser } from '@/hooks/useAuth';
import { useCompetitionContext, useCompetitionLandingByRef, useRequestToJoinCompetition } from '@/hooks/useCompetitions';
import { PageSpinner } from '@/components/ui/Spinner';
import { Alert, EmptyState } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { getApiErrorMessage } from '@/api/client';
import { CompetitionStatus, type CompetitionLanding } from '@/api/types';
import { competitionStatusLabel, competitionStatusTone, FZS_PATRONAGE_LABEL } from '@/lib/competitions';
import { formatDate, formatDateTime } from '@/lib/dates';
import { describeImageRequirements } from '@/lib/imageRequirements';

/** Slovenian plural after a number: 1 fotografija, 2 fotografiji, 3-4 fotografije, 5+ fotografij. */
function photoNoun(n: number): string {
  if (n % 100 === 1) return 'fotografija';
  if (n % 100 === 2) return 'fotografiji';
  if (n % 100 === 3 || n % 100 === 4) return 'fotografije';
  return 'fotografij';
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-fg">{title}</h2>
      {children}
    </section>
  );
}

function JoinPanel({ landing }: { landing: CompetitionLanding }) {
  const { isAuthenticated } = useCurrentUser();
  const context = useCompetitionContext(isAuthenticated ? landing.id : undefined);
  const requestToJoin = useRequestToJoinCompetition();
  const navigate = useNavigate();
  const competitionPath = `/competitions/${landing.slug}`;
  const afterLogin = { from: { pathname: competitionPath } };

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm text-fg-secondary">
          {landing.isOpenForSignup
            ? 'Za sodelovanje ustvarite račun ali se prijavite, če ga že imate.'
            : 'Prijave na to tekmovanje trenutno niso odprte.'}
        </p>
        <div className="flex flex-wrap gap-2">
          {/* Most visitors have no account yet, so creating one is the main action. */}
          {landing.isOpenForSignup && (
            <Link to={`/register?competition=${landing.id}`}>
              <Button>Ustvari račun in se prijavi</Button>
            </Link>
          )}
          <Link to="/login" state={afterLogin}>
            <Button variant={landing.isOpenForSignup ? 'secondary' : 'primary'}>Že imam račun</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (context.isLoading) return <PageSpinner />;
  const ctx = context.data;

  if (ctx && (ctx.isParticipant || ctx.isJudge || ctx.canManage)) {
    return (
      <Link to={competitionPath}>
        <Button>Odpri tekmovanje</Button>
      </Link>
    );
  }
  if (!landing.isOpenForSignup) {
    return <p className="text-sm text-fg-secondary">Prijave na to tekmovanje trenutno niso odprte.</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      {requestToJoin.isError && <Alert variant="error">{getApiErrorMessage(requestToJoin.error)}</Alert>}
      {requestToJoin.isSuccess ? (
        <p className="text-sm text-fg-secondary">Prijavljeni ste.</p>
      ) : (
        <Button
          isLoading={requestToJoin.isPending}
          onClick={() => requestToJoin.mutate(landing.id, { onSuccess: () => navigate(competitionPath) })}
          className="self-start"
        >
          Prijavi se na tekmovanje
        </Button>
      )}
    </div>
  );
}

/** Public announcement page of a competition ("razpis"): readable without an account. */
export function CompetitionLandingPage() {
  const { competitionRef } = useParams<{ competitionRef: string }>();
  const landing = useCompetitionLandingByRef(competitionRef);

  const data = landing.data;
  const logo = data?.logoUrl ?? data?.organizationLogoUrl;
  const requirementLines = describeImageRequirements(data?.imageRequirements);
  const submissionsOpen = data?.status === CompetitionStatus.Active;

  return (
    <div className="min-h-screen bg-surface-0 text-fg">
      <PublicHeader />

      <main className="mx-auto flex max-w-4xl flex-col gap-8 px-4 py-8">
        {landing.isLoading && <PageSpinner />}
        {landing.isError && (
          <EmptyState title="Razpis ni na voljo" description="Tekmovanje ne obstaja ali še ni objavljeno." />
        )}

        {data && (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                {logo && <img src={logo} alt={`${data.name} logotip`} className="h-14 w-14 rounded-photo object-contain" />}
                <div className="min-w-0">
                  <h1 className="font-display text-3xl font-semibold tracking-tight text-fg">{data.name}</h1>
                  {data.organizationName && <p className="text-sm text-fg-muted">Razpisuje: {data.organizationName}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge tone={competitionStatusTone(data.status)}>{competitionStatusLabel(data.status)}</Badge>
                {data.isFzsPatronage && <Badge tone="Fzs">{FZS_PATRONAGE_LABEL}</Badge>}
              </div>
              {data.description && <p className="whitespace-pre-line text-base text-fg-secondary">{data.description}</p>}
            </div>

            <Card>
              <CardBody className="grid gap-5 sm:grid-cols-2">
                <dl className="flex flex-col gap-2 text-sm">
                  <div className="flex items-start gap-2">
                    <CalendarClock size={16} className="mt-0.5 shrink-0 text-fg-muted" aria-hidden="true" />
                    <div>
                      <dt className="text-fg-muted">Sprejem del</dt>
                      <dd className="font-medium text-fg">
                        {data.submissionOpensAt ? `od ${formatDate(data.submissionOpensAt)} ` : ''}
                        {data.submissionDeadline ? `do ${formatDateTime(data.submissionDeadline)}` : data.submissionOpensAt ? '' : '—'}
                      </dd>
                    </div>
                  </div>
                  {data.judgingDeadline && (
                    <div className="flex items-start gap-2">
                      <Camera size={16} className="mt-0.5 shrink-0 text-fg-muted" aria-hidden="true" />
                      <div>
                        <dt className="text-fg-muted">Ocenjevanje do</dt>
                        <dd className="font-medium text-fg">{formatDateTime(data.judgingDeadline)}</dd>
                      </div>
                    </div>
                  )}
                  {(data.startDate || data.endDate) && (
                    <div className="flex items-start gap-2">
                      <CalendarClock size={16} className="mt-0.5 shrink-0 text-fg-muted" aria-hidden="true" />
                      <div>
                        <dt className="text-fg-muted">Trajanje tekmovanja</dt>
                        <dd className="font-medium text-fg">
                          {formatDate(data.startDate)} – {formatDate(data.endDate)}
                        </dd>
                      </div>
                    </div>
                  )}
                </dl>
                <div className="flex flex-col justify-center gap-2 sm:border-l sm:border-line sm:pl-5">
                  {submissionsOpen && <p className="text-sm font-medium text-success">Oddaja del je odprta.</p>}
                  <JoinPanel landing={data} />
                </div>
              </CardBody>
            </Card>

            {data.announcementUrl && (
              <Section title="Razpis">
                <a
                  href={data.announcementUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 self-start rounded-control border border-line bg-surface-1 px-4 py-3 text-sm hover:bg-surface-2"
                >
                  <FileText size={20} className="shrink-0 text-accent-text" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-fg">{data.announcementFileName ?? 'razpis.pdf'}</span>
                    <span className="block text-fg-muted">Razpis tekmovanja (PDF)</span>
                  </span>
                  <Download size={16} className="shrink-0 text-fg-muted" aria-hidden="true" />
                </a>
              </Section>
            )}

            {requirementLines.length > 0 && (
              <Section title="Tehnične zahteve za fotografije">
                <ul className="list-disc pl-5 text-sm text-fg-secondary">
                  {requirementLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <p className="text-xs text-fg-muted">
                  {data.imageRequirements.rejectNonCompliant
                    ? 'Fotografija, ki ne ustreza zahtevam, bo ob nalaganju zavrnjena.'
                    : 'Fotografija, ki ne ustreza zahtevam, bo označena, organizator pa jo lahko zavrne.'}
                </p>
              </Section>
            )}

            {data.categories.length > 0 && (
              <Section title="Kategorije">
                <ul className="flex flex-col gap-2">
                  {data.categories.map((c) => (
                    <li key={c.name} className="rounded-control border border-line bg-surface-1 px-3 py-2 text-sm">
                      <span className="font-medium text-fg">{c.name}</span>
                      <span className="text-fg-muted">
                        {' '}
                        · največ {c.maxSubmissionsPerUser} {photoNoun(c.maxSubmissionsPerUser)} na avtorja
                      </span>
                      {c.description && <p className="mt-0.5 text-fg-secondary">{c.description}</p>}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

          </>
        )}
      </main>
    </div>
  );
}
